/**
 * DayColumn Component
 * Renders a single day column with shift, task, and calendar event blocks.
 * Includes footer counts and @dnd-kit droppable support.
 */

import { useMemo, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { format } from 'date-fns';
import type { ShiftListItemDto, Task, ShiftStatus, CalendarEventDto as CalendarEventType } from '@signalvote/shared';
import {
  HOUR_HEIGHT,
  SNAP_MINUTES,
  SNAP_PX,
  MAX_SNAP_MINUTES,
  calculateEventPositions,
  timeToMinutes,
  minutesToTimeString,
  formatTime12Hour,
  getTimePartsInTimezone,
  type CalendarEvent,
} from '@/lib/calendarUtils';
import { EventBlock } from './EventBlock';
import { ShiftRosterBlock } from './ShiftRosterBlock';
import { partitionShiftsIntoRosterGroups, DEFAULT_SHIFT_MINUTES } from './shiftRoster';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface DayColumnProps {
  /** The date for this column */
  date: Date;
  /** Whether this column represents today */
  isToday: boolean;
  /** Whether this column is in the past */
  isPast?: boolean;
  /** Whether this column is the selected date */
  isSelected?: boolean;
  /** Number of shifts on this day */
  shiftCount: number;
  /** Number of tasks on this day */
  taskCount: number;
  /** Shifts to render for this day */
  shiftsForDay: ShiftListItemDto[];
  /** Tasks to render for this day */
  tasksForDay: Task[];
  /** Google/Microsoft calendar events for this day */
  calendarEvents?: CalendarEventType[];
  /** Get status for a shift by ID */
  getShiftStatus: (id: string) => ShiftStatus;
  /** Get claimer name for a shift by ID */
  getClaimedBy?: (id: string) => string | undefined;
  /** Handler when a shift block is clicked */
  onShiftClick: (shift: ShiftListItemDto) => void;
  /** Handler when an empty time slot is clicked */
  /** Omitted when the user may not create; the column then offers no slot to click. */
  onEmptySlotClick?: (date: Date, hour: number) => void;
  /** Handler when a task checkbox is clicked */
  onTaskCheckboxClick: (taskId: string) => void;
  /** Handler when a task duration is resized */
  onTaskResize?: (taskId: string, newDuration: number) => void;
  /** Handler when a task block is clicked */
  onTaskClick: (task: Task) => void;
  /** Handler when a calendar event is clicked */
  onCalendarEventClick?: (event: CalendarEventType) => void;
  /** IANA timezone for external calendar events (e.g. 'America/Chicago') */
  timeZoneId?: string;
  /** Children to render in the column body (events, indicators, etc.) */
  children?: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Single day column with event blocks (shifts, tasks, calendar events)
 * and footer showing shift/task counts.
 */
export function DayColumn({
  date,
  isToday,
  isPast,
  isSelected,
  shiftCount,
  taskCount,
  shiftsForDay,
  tasksForDay,
  calendarEvents: externalEvents = [],
  getShiftStatus,
  getClaimedBy,
  onShiftClick,
  onEmptySlotClick,
  onTaskCheckboxClick,
  onTaskResize,
  onTaskClick,
  onCalendarEventClick,
  timeZoneId,
  children,
}: DayColumnProps) {
  // Droppable setup
  const dateStr = format(date, 'yyyy-MM-dd');
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${dateStr}`,
    data: { date: dateStr, type: 'time-grid' },
  });

  // Build footer text based on counts
  const footerParts: string[] = [];
  if (shiftCount > 0) {
    footerParts.push(`${shiftCount} Shift${shiftCount !== 1 ? 's' : ''}`);
  }
  if (taskCount > 0) {
    footerParts.push(`${taskCount} Task${taskCount !== 1 ? 's' : ''}`);
  }
  const footerText = footerParts.length > 0 ? footerParts.join(', ') : '';

  // Hover preview band state
  const [hoverSlot, setHoverSlot] = useState<number | null>(null);

  // Which cascaded block the user last clicked. A cascaded block is half-covered
  // by its neighbour, so clicking one commits it to the front and it stays there
  // after the details dialog closes. Shifts and tasks cascade in separate
  // overlap groups, so each kind keeps its own raised block.
  const [raisedShiftId, setRaisedShiftId] = useState<string | null>(null);
  const [raisedTaskId, setRaisedTaskId] = useState<string | null>(null);

  const handleShiftClick = (shift: ShiftListItemDto) => {
    setRaisedShiftId(shift.id);
    onShiftClick(shift);
  };

  const handleTaskClick = (task: Task) => {
    setRaisedTaskId(task.id);
    onTaskClick(task);
  };

  // Handle empty slot click
  const handleBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onEmptySlotClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const hour = Math.floor(relativeY / HOUR_HEIGHT);
    const clampedHour = Math.max(0, Math.min(23, hour));
    onEmptySlotClick(date, clampedHour);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onEmptySlotClick) return;
    // Hide hover band when mouse is over an event block
    if ((e.target as HTMLElement).closest?.('[data-event-block]')) {
      setHoverSlot(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const rawMinutes = (relativeY / HOUR_HEIGHT) * 60;
    const snapped = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;
    const clamped = Math.max(0, Math.min(MAX_SNAP_MINUTES, snapped));
    setHoverSlot(clamped);
  };

  const handleMouseLeave = () => setHoverSlot(null);

  // Collapse overlapping shifts into roster groups wherever 3+ run at once;
  // the rest stay singles.
  // Tasks and external calendar events are never collapsed.
  const { groups: rosterGroups, singles: singleShifts } = useMemo(
    () => partitionShiftsIntoRosterGroups(shiftsForDay),
    [shiftsForDay]
  );

  // Calculate positions for overlapping events (side-by-side layout)
  const eventPositions = useMemo(() => {
    const calendarEvents: CalendarEvent[] = [];

    // Add single (un-collapsed) shifts
    for (const shift of singleShifts) {
      if (!shift.startTime) continue;
      const startMinutes = timeToMinutes(shift.startTime);
      const durationMinutes = shift.estimatedMinutes || DEFAULT_SHIFT_MINUTES;
      calendarEvents.push({
        id: shift.id,
        startMinutes,
        endMinutes: startMinutes + durationMinutes,
        type: 'shift',
      });
    }

    // Add one synthetic positioning event per roster group (collapsed block)
    for (const group of rosterGroups) {
      calendarEvents.push({
        id: group.id,
        startMinutes: group.startMinutes,
        endMinutes: group.endMinutes,
        type: 'shift',
      });
    }

    // Add scheduled tasks (with dueTime)
    for (const task of tasksForDay) {
      if (!task.dueTime) continue;
      const startMinutes = timeToMinutes(task.dueTime);
      const durationMinutes = task.durationMinutes || 60;
      calendarEvents.push({
        id: task.id,
        startMinutes,
        endMinutes: startMinutes + durationMinutes,
        type: 'task',
      });
    }

    // Add external calendar events (Google/Microsoft)
    for (const evt of externalEvents) {
      if (evt.isAllDay) continue; // All-day events are rendered separately
      const startParts = getTimePartsInTimezone(evt.startTime, timeZoneId);
      const endParts = getTimePartsInTimezone(evt.endTime, timeZoneId);
      const startMins = startParts.hours * 60 + startParts.minutes;
      const endMins = endParts.hours * 60 + endParts.minutes;
      calendarEvents.push({
        id: evt.id,
        startMinutes: startMins,
        endMinutes: Math.max(endMins > startMins ? endMins : 1440, startMins + 30), // minimum 30 min, handle midnight-spanning
        type: 'calendar-event',
      });
    }

    return calculateEventPositions(calendarEvents);
  }, [singleShifts, rosterGroups, tasksForDay, externalEvents, timeZoneId]);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col relative h-full border-r border-dotted border-gray-200 transition-colors',
        isOver && 'bg-blue-50/30',
        isToday && !isOver && 'bg-blue-50/20',
        isPast && !isOver && 'bg-gray-50/60',
        isSelected && !isToday && !isOver && 'bg-primary/5'
      )}
    >
      {/* Column Body - for events and time indicator */}
      <div
        data-testid="day-column-body"
        onClick={handleBodyClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className={cn('flex-1 relative', onEmptySlotClick && 'cursor-pointer', isPast && 'opacity-40')}
      >
        {/* Hour grid lines - one at each hour boundary */}
        {Array.from({ length: 23 }, (_, i) => (
          <div
            key={`grid-${i}`}
            className="absolute left-0 right-0 border-b border-dotted border-gray-200 pointer-events-none"
            style={{ top: (i + 1) * HOUR_HEIGHT }}
          />
        ))}
        {children}
        {hoverSlot !== null && (
          <div
            className="absolute left-0 right-0 bg-primary/8 pointer-events-none z-[1] flex items-center justify-center"
            style={{
              top: (hoverSlot / 60) * HOUR_HEIGHT,
              height: SNAP_PX,
            }}
          >
            <span className="text-[0.65rem] font-medium text-muted-foreground bg-background/80 px-2 py-0.5 rounded shadow-sm">
              {formatTime12Hour(minutesToTimeString(hoverSlot))}
            </span>
          </div>
        )}
        {/* Shift blocks (un-collapsed singles) */}
        {singleShifts.map((shift) => (
          <EventBlock
            key={shift.id}
            type="shift"
            shift={shift}
            status={getShiftStatus(shift.id)}
            claimedBy={getClaimedBy?.(shift.id)}
            onClick={handleShiftClick}
            position={eventPositions.get(shift.id)}
            isRaised={raisedShiftId === shift.id}
          />
        ))}
        {/* Collapsed roster blocks (3+ shifts sharing a start time) */}
        {rosterGroups.map((group) => (
          <ShiftRosterBlock
            key={group.id}
            group={group}
            position={eventPositions.get(group.id)}
            getShiftStatus={getShiftStatus}
            onShiftClick={handleShiftClick}
            isRaised={raisedShiftId === group.id}
            onRaise={() => setRaisedShiftId(group.id)}
          />
        ))}
        {/* Task Time Blocks (only scheduled tasks with dueTime) */}
        {tasksForDay
          .filter((task) => task.dueTime)
          .map((task) => (
            <EventBlock
              key={task.id}
              type="task"
              task={task}
              onCheckboxClick={onTaskCheckboxClick}
              onResize={onTaskResize}
              onClick={handleTaskClick}
              position={eventPositions.get(task.id)}
              isRaised={raisedTaskId === task.id}
            />
          ))}
        {/* External calendar events (Google/Microsoft) */}
        {externalEvents
          .filter((evt) => !evt.isAllDay)
          .map((evt) => (
            <EventBlock
              key={evt.id}
              type="calendar-event"
              event={evt}
              onClick={onCalendarEventClick}
              position={eventPositions.get(evt.id)}
              timeZoneId={timeZoneId}
            />
          ))}
      </div>

      {/* Day Footer */}
      {footerText && (
        <div
          className={cn(
            'sticky bottom-0 z-[2] py-1 px-1 text-center border-t border-dotted border-gray-200',
            isToday ? 'bg-blue-50/30' : isSelected ? 'bg-primary/5' : 'bg-white'
          )}
        >
          <span className="text-[0.65rem] text-gray-400">
            {footerText}
          </span>
        </div>
      )}
    </div>
  );
}

export default DayColumn;
